<script lang="ts">
const kbdCls = cva("kbd", {
	variants: {
		size: {
			xs: "kbd-xs",
			sm: "kbd-sm",
			md: "kbd-md",
			lg: "kbd-lg",
		},
	},
	defaultVariants: {
		size: "md",
	},
});

type variants = VariantProps<typeof kbdCls>;

export interface KeyboardShortcutProps<T extends string> extends ClassProps {
	command: Key<T>;
	prevent?: boolean;
	disableOnInput?: boolean;
	size?: variants["size"];
}

export type KeyboardShortcutEmits = {
	(evt: "active"): void;
};
</script>

<script setup lang="ts" generic="T extends string">
import { cva, type VariantProps } from "class-variance-authority";
import { computed } from "vue";
import type { ClassProps } from "../../lib/base-props";
import { useKeys, type Key } from "../../lib/keys";
import { cn } from "../../lib/utils";

const props = defineProps<KeyboardShortcutProps<T>>();

const emits = defineEmits<KeyboardShortcutEmits>();

const isMac = navigator.platform.toUpperCase().indexOf("MAC") >= 0;

const platformCommand = computed(() => {
	let replace: (k: string) => string;
	if (isMac) {
		replace = (k) => k.replace("ctrl", "cmd");
	} else {
		replace = (k) => k.replace("cmd", "ctrl");
	}

	return replace(props.command) as Key<T>;
});

const displayCommand = computed(() => {
	return platformCommand.value
		.split("+")
		.map((key) => (key.length === 1 ? key.toUpperCase() : key))
		.map((key) => key.replaceAll("ctrl", "Ctrl").replaceAll("cmd", "⌘"))
		.join(" ");
});

useKeys(
	() => [platformCommand.value],
	() => {
		emits("active");
	},
	() => ({ prevent: props.prevent, disableOnInput: props.disableOnInput })
);
</script>

<template>
	<div class="flex gap-1">
		<kbd :class="cn(kbdCls(props))">{{ displayCommand }}</kbd>
	</div>
</template>
